import Channel, { ThreadChannel } from '@@types/Channel';
import { ChannelMemberReadSeqUpdateData } from '@@types/websocket/Member';

import { ChannelFlag, ChannelMemberRole, ChannelType, MAIN_THREAD_CHANNEL_ID, MODAL_NAMES } from '~/constants';
import { isDirectChannel } from '~/services/channelService';
import WebsocketConnector from '~/services/WebsocketConnector';
import Actions from '~/stores/Actions';
import { channelLogsStore } from '~/stores/ChannelLogsStore';
import { channelManager } from '~/stores/ChannelManager';
import { channelMembersLangStore } from '~/stores/ChannelMembersLangStore';
import { channelReadSeqStore } from '~/stores/ChannelReadSeqStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { forwardLogStore } from '~/stores/ForwardLogStore';
import { threadChannelLogsStore } from '~/stores/ThreadChannelLogsStore';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { then } from '~/utils/monadUtil';
import { isPopupView } from '~/utils/runEnvironmentUtil';
import { append, omit, path, remove, uniq } from 'ramda';
import contains from 'ramda/es/contains';
import forEach from 'ramda/es/forEach';
import pipe from 'ramda/es/pipe';
import prop from 'ramda/es/prop';

class ChannelMessageHandler {
  constructor(private ws: WebsocketConnector, private actions: Actions) {}

  public handleArchive = (data: { action: string; archivedAt: number; channelId: string; title: string }) => {
    channelManager.updateChannel({
      archivedAt: data.archivedAt,
      channelId: data.channelId,
      title: data.title,
      flag: ChannelFlag.Archived,
    });
  };

  public handleUnarchive = (data: { action: string; channelId: string }) => {
    channelManager.updateChannel({
      archivedAt: null,
      channelId: data.channelId,
      flag: ChannelFlag.Normal,
    });
  };

  public handleCreate = (channel: Channel) => {
    if (channel.type === ChannelType.Thread) {
      this.handleThreadChannelCreate(channel as ThreadChannel);
      return;
    }
    channelsStore.addChannel(channel);

    // 채널 생성 메시지가 새창으로도 전달되기 때문에 본창에 한해서만 처리되도록 조건 추가
    if (uiState.isMainView && channel.role === ChannelMemberRole.Creator && channel.token !== 'false') {
      this.actions.channelActions.selectChannel(channel.channelId);
      uiState.clearGnbSearchKeyword();
    }

    if (isDirectChannel(channel)) {
      this.actions.memberActions.fetchMember(channel.opponentId);
    }
  };

  private handleThreadChannelCreate = (channel: ThreadChannel) => {
    if (
      channel.parentChannelLogId === threadChannelLogsStore.dummyParentLogId &&
      threadChannelLogsStore.ignoreThreadChannelCreate !== channel.parentChannelLogId
    ) {
      threadDetailUiState.maybeMainEditor().map((editor) => {
        this.actions.threadMainEditorActions.storeCurrentValueDraftLog(channel.channelId, editor);
      });
      this.actions.threadChannelActions.openThreadChannel(channel.channelId);
    }
    threadChannelStore.addChannel(channel);
    channelLogsStore.updateThreadInfo(channel);
  };

  handleDisplay = (p: { channelId: string; displayed: boolean; displayedChangedAt: number }) => {
    channelsStore.updateChannel(p);
  };

  handleUpdate = (channel: Partial<Channel>) => {
    channelManager.updateChannel(channel);
  };

  handleReadSeqUpdate = (params: ChannelMemberReadSeqUpdateData) => {
    channelReadSeqStore.setCurrentMemberReadSeq(params);
    if (params.memberId === userStore.id) {
      channelManager.updateChannel(omit(['memberId'], params));
    }
    if (params.unreadCount === 0) {
      const threadChannel = threadChannelStore.getById(params.channelId);
      threadChannelStore.updateChannel({ ...threadChannel, unreadFlag: false });
    }
  };

  handleAdminRegister = (data: { channelId: string; adminId: string }) => {
    pipe(
      channelsStore.getById,
      path(['management', 'adminIds']),
      append(data.adminId),
      uniq,
      channelsStore.setAdminMembers(data.channelId)
    )(data.channelId);
  };

  handleAdminRemove = (data: { channelId: string; adminId: string }) => {
    const adminIds = channelsStore.getAdminMembers(data.channelId);
    const index = adminIds.indexOf(data.adminId);
    const newAdminMembers = remove(index, 1, adminIds);
    channelsStore.setAdminMembers(data.channelId, newAdminMembers);
  };

  handleManagementUpdate = (management: { channelId: string; adminIds: string[]; adminOnly: boolean }) => {
    channelsStore.setChannelManagement(management);
  };

  handleMemberJoin = (data: { channelId: string; memberIds: string[] }, version?: 'v4') => {
    if (contains(userStore.id, data.memberIds)) {
      this.handleInvitedChannelFirstly(data.channelId, version === 'v4');
    } else {
      if (mainDetailUiState.contentsChannelId === data.channelId) {
        this.actions.channelMembersActions.debouncedFetchChannelReadSeq(data.channelId);
        const isSharedChannel = channelsStore.isSharedChannelId(data.channelId);
        !isSharedChannel && channelMembersLangStore.fetchAndCache(data.channelId);
        isSharedChannel
          ? this.actions.channelActions
              .fetchSharedTenantsList(data.channelId)
              .then(() => this.actions.channelMembersActions.fetchInterTenantChannelMembersById(data.channelId))
          : this.actions.memberActions.fetchBulkMembers(data.memberIds);
      }

      channelsStore.appendMembers(data.channelId, data.memberIds);
    }
  };

  handleMemberLeave = (data: { channelId: string; memberId: string }) => {
    const { channelId, memberId } = data;
    if (channelId === mainDetailUiState.contentsChannelId) {
      channelsStore.isSharedChannelId(data.channelId)
        ? this.actions.channelActions.fetchSharedTenantsList(data.channelId)
        : channelMembersLangStore.removeMembers(channelId, [memberId]);
    } else if (mainDetailUiState.contentsChannelId === MAIN_THREAD_CHANNEL_ID) {
      threadChannelStore.handleLeaveChannelInMainThreadChannel(channelId);
    }

    if (memberId !== userStore.id) {
      this.actions.channelMembersActions.debouncedFetchChannelReadSeq(channelId);
      this.handleNonUserLeave(data);
      if (channelId === mainDetailUiState.contentsChannelId) {
        this.actions.officeHoursActions.revalidateChannelNotInWork();
      }
      return;
    }

    if (threadChannelLogsStore.threadPanelParentLog?.channelId) {
      this.actions.threadChannelActions.clearThreadPanel();
    }

    this.handleUserLeave(data);
  };

  handleMemberRemove = (data: { channelId: string; memberIds: string[] }) => {
    forEach((memberId) => {
      this.handleMemberLeave({ memberId, channelId: data.channelId });
    }, data.memberIds);
  };

  handleUpdatePreference = (params) => {
    channelManager.updatePreferences(params);
  };

  handleUpdateTranslation = (params) => {
    channelManager.updateChannel(params);
  };

  private handleInvitedChannelFirstly = (channelId: string, isSharedChannel: boolean) => {
    const channel = channelsStore.getById(channelId);
    if (channel) return;
    pipe(
      channelsStore.fetchById(isSharedChannel),
      then(this.actions.channelMembersActions.getOrFetchChannelMembers)
    )(channelId);
  };

  private handleNonUserLeave = (data: { channelId: string; memberId: string }) => {
    channelsStore.removeMember(data.channelId, data.memberId);
  };

  private handleUserLeave = (data: { channelId: string; memberId: string }) => {
    if (mainDetailUiState.contentsChannelId === data.channelId && isPopupView()) {
      return window.close();
    }

    const updateChannelModal = uiState.modalMap[MODAL_NAMES.UPDATE_CHANNEL];
    if (updateChannelModal && updateChannelModal?.args.channelId === data.channelId) {
      uiState.closeModal(MODAL_NAMES.UPDATE_CHANNEL);
    }

    if (data.channelId === mainDetailUiState.contentsChannelId) {
      pipe(prop('channelId'), channelsStore.getFirstChannelIdExcept, this.actions.channelActions.selectChannel)(data);
    }
    this.deleteChannel(data.channelId);
  };

  private deleteChannel = (channelId: string) => {
    uiState.setIsDeletingChannel(true);
    channelsStore.deleteChannel(channelId);
    forwardLogStore.removeForwardLogByLogChannelId(channelId);
    setTimeout(() => {
      uiState.setIsDeletingChannel(false);
    });
  };
}

export default ChannelMessageHandler;
